// Folder upload, browser side (DEC-180, UI_SDD §4.1, SDD §17.12).
//
// A browser never reveals a folder's path, only its name and its files. So picking a
// folder reads the file list here and sends nothing; the preview is computed from that
// list; and only "Scan folder" compresses and uploads.
//
// What is left out is never silent (§0.6): every skipped folder is listed with its file
// count, in the preview and in what the scan records.

import { AsyncZipDeflate, Zip, ZipDeflate } from 'fflate'
import type { ScanSourcesResponse, UploadSourceManifest } from '../types/api'

export interface ExcludedFolder {
  path: string
  file_count: number
}

export interface FolderPreview {
  folderName: string
  /** Files that will be uploaded, with their path inside the folder. */
  included: { path: string; file: File }[]
  fileCount: number
  /** Bytes of the included files, uncompressed, not counting `.git`. */
  bytes: number
  /** Whether the picked files contain a root `.git` folder. */
  hasGit: boolean
  gitFileCount: number
  gitBytes: number
  excluded: ExcludedFolder[]
}

/** The path a picked file has inside the picked folder, without the folder's own name. */
function relativeParts(file: File): string[] {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
  return rel.split('/').filter(Boolean)
}

/**
 * Sort the picked files into what will be sent and what is left out.
 *
 * A file is left out when any folder on its path is in `excludedDirs` (served by the
 * server, so it matches what the scanner skips). It is recorded under the outermost such
 * folder, so `node_modules/a/node_modules/b.js` counts once, under `node_modules`.
 */
export function buildPreview(
  files: File[],
  excludedDirs: string[],
  includeGit: boolean,
): FolderPreview {
  const skip = new Set(excludedDirs)
  const excludedCounts = new Map<string, number>()
  const included: { path: string; file: File }[] = []
  let folderName = ''
  let bytes = 0
  let gitFileCount = 0
  let gitBytes = 0

  for (const file of files) {
    const parts = relativeParts(file)
    if (parts.length < 2) continue
    folderName ||= parts[0]
    const inner = parts.slice(1)

    if (inner[0] === '.git') {
      gitFileCount += 1
      gitBytes += file.size
      if (includeGit) included.push({ path: inner.join('/'), file })
      continue
    }

    const skipAt = inner.slice(0, -1).findIndex((dir) => skip.has(dir))
    if (skipAt >= 0) {
      const key = inner.slice(0, skipAt + 1).join('/')
      excludedCounts.set(key, (excludedCounts.get(key) ?? 0) + 1)
      continue
    }

    included.push({ path: inner.join('/'), file })
    bytes += file.size
  }

  const excluded = [...excludedCounts.entries()]
    .map(([path, file_count]) => ({ path, file_count }))
    .sort((a, b) => b.file_count - a.file_count || a.path.localeCompare(b.path))

  return {
    folderName,
    included,
    // The project's own files. `.git` internals are counted separately, and counting them
    // here made a 19-file project read as 41.
    fileCount: included.length - (includeGit ? gitFileCount : 0),
    bytes,
    hasGit: gitFileCount > 0,
    gitFileCount,
    gitBytes,
    excluded,
  }
}

export class UploadTooLargeError extends Error {
  constructor(public readonly limitBytes: number) {
    super(`The compressed folder is over the ${formatBytes(limitBytes)} limit.`)
    this.name = 'UploadTooLargeError'
  }
}

/**
 * Compress the included files into one zip, streaming, so memory holds the compressed
 * output and one file at a time rather than the whole folder twice.
 *
 * Stops as soon as the output passes `limitBytes`, rather than finishing a zip the server
 * would refuse. Uses worker-based compression when the browser has workers, so a large
 * folder does not freeze the page.
 */
export function zipFolder(
  included: { path: string; file: File }[],
  options: {
    limitBytes: number
    onProgress?: (fraction: number) => void
    signal?: AbortSignal
  },
): Promise<Blob> {
  const totalBytes = included.reduce((n, f) => n + f.file.size, 0) || 1
  const useWorkers = typeof Worker !== 'undefined'

  return new Promise<Blob>((resolve, reject) => {
    const chunks: Uint8Array[] = []
    let outBytes = 0
    let failed = false

    const fail = (err: unknown) => {
      if (failed) return
      failed = true
      zip.terminate()
      reject(err)
    }

    const zip = new Zip((err, data, final) => {
      if (failed) return
      if (err) {
        fail(err)
        return
      }
      chunks.push(data)
      outBytes += data.length
      if (outBytes > options.limitBytes) {
        fail(new UploadTooLargeError(options.limitBytes))
        return
      }
      if (final) resolve(new Blob(chunks as BlobPart[], { type: 'application/zip' }))
    })

    options.signal?.addEventListener('abort', () =>
      fail(new DOMException('Upload cancelled', 'AbortError')),
    )

    void (async () => {
      let readBytes = 0
      for (const { path, file } of included) {
        if (failed) return
        const entry = useWorkers ? new AsyncZipDeflate(path, { level: 6 }) : new ZipDeflate(path, { level: 6 })
        zip.add(entry)
        const data = new Uint8Array(await file.arrayBuffer())
        entry.push(data, true)
        readBytes += file.size
        options.onProgress?.(readBytes / totalBytes)
      }
      if (!failed) zip.end()
    })().catch(fail)
  })
}

/** "1 file", "2 files". */
export function filesLabel(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'file' : 'files'}`
}

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${Math.round(n / 1024)} KB`
  return `${n} B`
}

/** Why the folder cannot be sent as it is, or null. Shown before anything is uploaded. */
export function previewProblem(
  preview: FolderPreview | null,
  limits: ScanSourcesResponse['upload'] | null,
  includeGit: boolean,
): string | null {
  if (!preview || !limits) return null
  if (preview.fileCount === 0) {
    return 'Nothing is left to scan in this folder after leaving out the folders above.'
  }
  if (preview.fileCount > limits.max_files) {
    return `This folder has ${filesLabel(preview.fileCount)} after leaving out the folders above. The limit is ${limits.max_files.toLocaleString()}.`
  }
  const unpacked = preview.bytes + (includeGit ? preview.gitBytes : 0)
  const maxUnpacked = limits.max_unpacked_mb * 1024 * 1024
  if (unpacked > maxUnpacked) {
    return `This folder is ${formatBytes(unpacked)} after leaving out the folders above. The limit is ${formatBytes(maxUnpacked)}.`
  }
  return null
}

/** One line for the Monitor and report headers: what an uploaded folder contained. */
export function sourceManifestSentence(m: UploadSourceManifest): string {
  const parts = [
    `Uploaded folder ${m.folder_name}`,
    filesLabel(m.file_count),
    m.has_git_history ? 'git history included' : 'git history not included',
  ]
  if (m.excluded.length) parts.push(`left out: ${m.excluded.map((e) => e.path).join(', ')}`)
  return parts.join(' · ')
}
